import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import {
  CitizenshipField,
  CountryField,
  EmailRequiredInfo,
  EmploymentOccupationFields,
  SSNField,
} from "../../../../../components/guardian/ManualEntryFields";
import {
  retrievalLabeledCreatableDisabledProps,
  retrievalShowsEmailRow,
  retrievalShowsOccupationBlock,
  retrievalShowsRelationshipRelationshipField,
  isHighBondAmount,
  getOccupationQuestionLabel,
  hasPiiAccessForSsn,
  gVal,
} from "../../retrievalGuardianUtils";

export default function RetrievalManualSection({
  index,
  guardian,
  guardians,
  globalData,
  retrievalCfg: cfg,
  keys,
  role,
  pii_access,
  isDisabled,
  allCities,
  allStates,
  loadingCitiesStates,
  GoogleAutoComplete,
  LabeledCreatableSelect,
  showSSNStates,
  toggleShowSSN,
  handleNameChange,
  handleFieldChange,
  handleZipCodeChange,
  handleGoogleCallback,
  handleEmploymentOccupationChange,
  splitEmploymentOccupation,
}) {
  const nameSx = cfg.manualStyles?.name || {};
  const selectSpread = retrievalLabeledCreatableDisabledProps(
    cfg.labeledSelectDisabledPropName,
    isDisabled
  );
  const esignatures = globalData?.basicInformation?.is_esignatures === "Yes";
  const bondAmt =
    globalData?.basicInformation?.bondAmount ?? globalData?.bond_amount;

  const showEmailBlock = retrievalShowsEmailRow(guardian, globalData, cfg);

  const showRelationship = retrievalShowsRelationshipRelationshipField(
    cfg,
    guardian
  );
  const showOccupation = retrievalShowsOccupationBlock(
    guardian,
    globalData,
    cfg
  );
  const showSsnBlock =
    isHighBondAmount(bondAmt) && hasPiiAccessForSsn(role, pii_access);

  return (
    <Box className="grid grid-cols-1 gap-4">
      <TextField
        sx={{ ...nameSx }}
        label="Name"
        value={gVal(guardian, keys, "name")}
        disabled={isDisabled}
        onChange={(e) => handleNameChange(index, e.target.value, guardians)}
        fullWidth
        required
        slotProps={{ htmlInput: { maxLength: 255 }}}
      />

      {showEmailBlock && (
        <>
          {cfg.manualEmailBanner && (
            <EmailRequiredInfo
              isNewPg={Boolean(guardian.is_new_pg)}
              isEsignatures={esignatures}
            />
          )}
          <TextField
            sx={{ mb: 2 }}
            label={cfg.emailFieldLabel || "Email"}
            value={gVal(guardian, keys, "email")}
            disabled={isDisabled}
            onChange={(e) =>
              handleFieldChange(index, keys.email, e.target.value, guardians)
            }
            fullWidth
            required
          />
        </>
      )}

      <CitizenshipField
        value={guardian[keys.citizenship]}
        customValue={guardian[keys.customCitizenship]}
        citizenshipSelectSx={cfg.manualStyles?.citizenshipSelect}
        specifyCitizenshipSx={cfg.manualStyles?.specifyCitizenship}
        onChange={(value) =>
          handleFieldChange(index, keys.citizenship, value, guardians)
        }
        onCustomChange={(value) =>
          handleFieldChange(
            index,
            keys.customCitizenship,
            value,
            guardians
          )
        }
        disabled={isDisabled}
      />

      <CountryField
        value={guardian[keys.country]}
        customValue={guardian[keys.customCountry]}
        countrySelectSx={cfg.manualStyles?.countrySelect}
        specifyCountrySx={cfg.manualStyles?.specifyCountry}
        onChange={(value) =>
          handleFieldChange(index, keys.country, value, guardians)
        }
        onCustomChange={(value) =>
          handleFieldChange(index, keys.customCountry, value, guardians)
        }
        disabled={isDisabled}
      />

      {showRelationship && (
        <TextField
          label={cfg.relationshipLabel}
          value={gVal(guardian, keys, "relationship")}
          disabled={isDisabled}
          onChange={(e) =>
            handleFieldChange(
              index,
              keys.relationship,
              e.target.value,
              guardians
            )
          }
          fullWidth
          required={cfg.relationshipRequired}
        />
      )}

      <Box>
        <Typography
          sx={{
            fontSize: "12px",
            color: "rgba(0, 0, 0, 0.6)",
            marginBottom: "4px",
            fontWeight: 500,
          }}
        >
          Search Address
        </Typography>
        <GoogleAutoComplete
          placeholder={cfg.googlePlaceholderManual || "Search Address"}
          callback={(address, city, state, zipCode) =>
            handleGoogleCallback(index, address, city, state, zipCode)
          }
        />
      </Box>

      <TextField
        sx={{ mt: 1, mb: 1 }}
        label="Street Address"
        value={gVal(guardian, keys, "address")}
        disabled={isDisabled}
        onChange={(e) =>
          handleFieldChange(index, keys.address, e.target.value, guardians)
        }
        fullWidth
        required
      />

      <TextField
        label="ZIP/Postal Code"
        value={gVal(guardian, keys, "zip")}
        disabled={isDisabled}
        onChange={(e) => handleZipCodeChange(e, index)}
        fullWidth
        required
      />

      {loadingCitiesStates ? (
        <Box display="flex" justifyContent="center" my={3}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <LabeledCreatableSelect
            label="City"
            options={allCities.map((c) => ({
              label: c,
              value: c,
            }))}
            value={
              gVal(guardian, keys, "city")
                ? {
                    label: gVal(guardian, keys, "city"),
                    value: gVal(guardian, keys, "city"),
                  }
                : null
            }
            onChange={(opt) =>
              handleFieldChange(
                index,
                keys.city,
                opt ? opt.value : "",
                guardians
              )
            }
            placeholder="Select or add a city"
            {...selectSpread}
          />
          <LabeledCreatableSelect
            label="State"
            options={allStates.map((srow) => ({
              label: srow,
              value: srow,
            }))}
            {...selectSpread}
            value={
              gVal(guardian, keys, "state")
                ? {
                    label: gVal(guardian, keys, "state"),
                    value: gVal(guardian, keys, "state"),
                  }
                : null
            }
            onChange={(opt) =>
              handleFieldChange(
                index,
                keys.state,
                opt ? opt.value : "",
                guardians
              )
            }
            placeholder="Select or add a state"
          />
        </>
      )}

      {showSsnBlock && (
        <SSNField
          value={gVal(guardian, keys, "ssn")}
          showSSN={showSSNStates[index]}
          onToggleVisibility={() => toggleShowSSN(index)}
          onChange={(value) =>
            handleFieldChange(index, keys.ssn, value, guardians)
          }
          disabled={isDisabled}
          hasPiiAccess={Boolean(pii_access)}
        />
      )}

      {showOccupation &&
        (() => {
          const { employmentStatus, occupationText } =
            splitEmploymentOccupation(
              guardian[keys.occupation] ??
                ""
            );
          const questionLabel = getOccupationQuestionLabel(employmentStatus);

          return (
            <EmploymentOccupationFields
              employmentStatus={employmentStatus}
              occupationText={occupationText}
              questionLabel={questionLabel}
              onChange={(status, text) =>
                handleEmploymentOccupationChange(
                  index,
                  status,
                  text,
                  guardians
                )
              }
              disabled={isDisabled}
            />
          );
        })()}
    </Box>
  );
}

RetrievalManualSection.propTypes = {
  index: PropTypes.number.isRequired,
  guardian: PropTypes.object.isRequired,
  guardians: PropTypes.array.isRequired,
  globalData: PropTypes.object,
  retrievalCfg: PropTypes.object.isRequired,
  keys: PropTypes.object.isRequired,
  role: PropTypes.string,
  pii_access: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isDisabled: PropTypes.bool,
  allCities: PropTypes.array.isRequired,
  allStates: PropTypes.array.isRequired,
  loadingCitiesStates: PropTypes.bool,
  GoogleAutoComplete: PropTypes.elementType.isRequired,
  LabeledCreatableSelect: PropTypes.elementType.isRequired,
  showSSNStates: PropTypes.object.isRequired,
  toggleShowSSN: PropTypes.func.isRequired,
  handleNameChange: PropTypes.func.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  handleZipCodeChange: PropTypes.func.isRequired,
  handleGoogleCallback: PropTypes.func.isRequired,
  handleEmploymentOccupationChange: PropTypes.func.isRequired,
  splitEmploymentOccupation: PropTypes.func.isRequired,
};
